/**
 * Your profile — the one screen in this app that is entirely YOURS.
 *
 * Everyone gets it, kids included, and every field on it changes only the person
 * looking at it: the Worker derives the member from the verified Access identity
 * and there is no id in any of these calls to argue with (worker/routes/profile.ts).
 * Role and email are not here on purpose — a grown-up sets those in
 * Settings → Family.
 *
 * Save model:
 *   - Picture saves the moment you pick it (a file picker that does nothing until
 *     you find another button later is how you lose a nine-year-old).
 *   - Everything else is a draft until you press Save, and the bar at the bottom
 *     says so the whole time.
 *
 * SAVE IS TWO WRITES, AND IT IS HONEST ABOUT THAT. Name/color go to
 * PUT /profile; notification opt-outs go to PUT /profile/notifications. They are
 * separate commits, so half of a save CAN land. This screen therefore tracks each
 * write on its own, re-reads the server when one of them fails so it is reporting
 * a fact rather than a guess, and says exactly which half saved. It never says
 * "nothing was changed" — that was a sentence the old code could not possibly know
 * to be true — and it never throws away what a person typed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { NotificationPref, NotificationType, Profile } from '../../shared/types';
import AvatarField from './AvatarField';
import ColorField, { isHex } from './ColorField';
import NotificationsField from './NotificationsField';
import { notifyCatalogFor } from './meta';
import { AlertNote, Button, ErrorNote, Field, InitialAvatar, Section, Skeleton, TextInput, cx } from './ui';

const NAME_MAX = 40;

type NotifyMap = Record<NotificationType, boolean>;

interface Draft {
  name: string;
  color: string;
  notify: NotifyMap;
}

/** What we actually know about one of the two writes once the dust settles. */
type Outcome = 'saved' | 'failed' | 'unknown';

/**
 * NO ROW MEANS ON. A type with no saved row is on; only `enabled === false` mutes.
 * Reading a missing row as "off" here would show the whole family a screen that
 * says they are muted when they are not.
 */
function toNotifyMap(role: Profile['member']['role'], rows: NotificationPref[]): NotifyMap {
  const map = {} as NotifyMap;
  for (const item of notifyCatalogFor(role)) map[item.type] = true;
  for (const row of rows) {
    if (row.type in map) map[row.type] = row.enabled;
  }
  return map;
}

function draftOf(profile: Profile): Draft {
  return {
    name: profile.member.name,
    color: profile.member.color,
    notify: toNotifyMap(profile.member.role, profile.notification_prefs),
  };
}

function explainLoad(e: unknown): string {
  if (e instanceof ApiRequestError) {
    if (e.status === 403) return 'You are signed in, but not set up as a family member yet. Ask a grown-up to add you in Settings → Family.';
    if (e.status >= 500) return 'The app answered with an error. Try again in a moment.';
    return e.message;
  }
  return 'Could not load your profile. Check your connection and try again.';
}

/**
 * The reason a write failed — and ONLY a reason we were actually told. A server
 * error carries its own sentence. A request that never came back tells us one
 * thing and one thing only: it never came back. We do not name a cause we did not
 * check ("check your connection" is a guess when the Worker may simply be down).
 */
function reasonOf(e: unknown): string {
  const raw = e instanceof ApiRequestError ? e.message.trim() : '';
  if (!raw) return 'The app didn’t answer.';
  return /[.!?]$/.test(raw) ? raw : `${raw}.`;
}

/** "your name", "your name and color", "your name, color and notifications" */
function listOf(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function sentence(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function ProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [identityChanged, setIdentityChanged] = useState(false);
  /** The hero picture wouldn't render. Fall back to the initial rather than a broken icon. */
  const [heroBroken, setHeroBroken] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    setProfile(null);
    try {
      const next = await api.get<Profile>('/profile');
      setProfile(next);
      setDraft(draftOf(next));
    } catch (e) {
      setLoadError(explainLoad(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // A new picture gets a clean shot at rendering; the last one's failure says nothing
  // about this one.
  useEffect(() => {
    setHeroBroken(false);
  }, [profile?.avatar_url]);

  /**
   * The picture changed — and ONLY the picture.
   *
   * The avatar saves on its own, the moment you pick it; it is not part of the save bar.
   * So it merges the new picture into the server row and leaves the draft completely alone.
   *
   * This used to call the full adopt(), which reset the draft from the server — meaning a
   * successful avatar upload silently threw away every other unsaved edit. Type a new name,
   * pick a color, mute a notification, then change your picture: name, color and mute all
   * gone, replaced by the server's old values, while the save bar cheerfully reported
   * "Everything here is saved."
   *
   * Losing what someone typed is bad. Telling them it was saved is worse.
   */
  const adoptAvatarOnly = useCallback((next: Profile) => {
    setProfile((prev) =>
      prev
        ? {
            ...prev,
            member: { ...prev.member, avatar_key: next.member.avatar_key },
            avatar_url: next.avatar_url,
          }
        : next
    );
    // draft is deliberately untouched — the family's unsaved edits survive.
  }, []);

  const role = profile?.member.role ?? 'child';

  const nameError =
    draft && draft.name.trim().length === 0
      ? 'Your name can’t be empty.'
      : draft && draft.name.trim().length > NAME_MAX
        ? `That’s longer than ${NAME_MAX} characters.`
        : null;
  const colorError = draft && !isHex(draft.color) ? 'A color looks like #2f6f4e.' : null;

  const changed = useMemo(() => {
    const none = {
      any: false,
      name: false,
      color: false,
      identity: false,
      notify: [] as NotificationType[],
    };
    if (!profile || !draft) return none;

    const base = draftOf(profile);
    const name = draft.name.trim() !== base.name;
    const color = draft.color.trim().toLowerCase() !== base.color.toLowerCase();
    const notify = notifyCatalogFor(profile.member.role)
      .map((n) => n.type)
      .filter((t) => draft.notify[t] !== base.notify[t]);

    return {
      any: name || color || notify.length > 0,
      name,
      color,
      identity: name || color,
      notify,
    };
  }, [profile, draft]);

  /**
   * Two writes, tracked separately.
   *
   * The old version wrapped both in one try/catch and, on any throw, told the family
   * "Nothing was changed" — which was false the moment the first write had already
   * committed, and which it had no way of knowing anyway. Now: each write is judged
   * on its own; a failure triggers a re-read of the server so the screen reports the
   * row that actually exists instead of guessing; whatever did not land stays in the
   * draft, exactly as typed, ready for another press of Save.
   */
  async function save() {
    if (!profile || !draft || nameError || colorError || !changed.any) return;

    setSaving(true);
    setSaveError(null);
    setSaved(false);

    // What each write is responsible for, in the words a person would use.
    const profileParts: string[] = [];
    const patch: Record<string, unknown> = {};
    if (changed.name) {
      patch.name = draft.name.trim();
      profileParts.push('your name');
    }
    if (changed.color) {
      patch.color = draft.color.trim().toLowerCase();
      profileParts.push('your color');
    }

    const wantProfile = profileParts.length > 0;
    const wantNotify = changed.notify.length > 0;

    let next: Profile = profile;
    let profileOutcome: Outcome = wantProfile ? 'unknown' : 'saved';
    let notifyOutcome: Outcome = wantNotify ? 'unknown' : 'saved';
    // Each write keeps its OWN reason. Two writes can fail for two different reasons,
    // and pinning one write's cause on the other is exactly the kind of invented
    // explanation this screen is not allowed to give.
    let profileReason: string | null = null;
    let notifyReason: string | null = null;

    if (wantProfile) {
      try {
        next = await api.put<Profile>('/profile', patch);
        profileOutcome = 'saved';
      } catch (e) {
        profileReason = reasonOf(e);
      }
    }

    // Independent of the first: a failed name change is no reason to silently drop a
    // notification change the person also made.
    if (wantNotify) {
      try {
        const prefs = await api.put<NotificationPref[]>('/profile/notifications', {
          prefs: changed.notify.map((type) => ({ type, enabled: draft.notify[type] })),
        });
        next = { ...next, notification_prefs: prefs };
        notifyOutcome = 'saved';
      } catch (e) {
        notifyReason = reasonOf(e);
      }
    }

    // A throw proves the call failed; it does NOT prove the write didn't land (the
    // connection can drop after D1 has committed). So when anything went wrong, ask
    // the server what is actually in the row and report THAT.
    if (profileOutcome === 'unknown' || notifyOutcome === 'unknown') {
      try {
        const truth = await api.get<Profile>('/profile');
        const t = draftOf(truth);
        if (profileOutcome === 'unknown') {
          const landed =
            (!changed.name || t.name === draft.name.trim()) &&
            (!changed.color || t.color.toLowerCase() === draft.color.trim().toLowerCase());
          profileOutcome = landed ? 'saved' : 'failed';
        }
        if (notifyOutcome === 'unknown') {
          const landed = changed.notify.every((type) => t.notify[type] === draft.notify[type]);
          notifyOutcome = landed ? 'saved' : 'failed';
        }
        next = truth;
      } catch {
        // Couldn't reach the server to check either. 'unknown' stays 'unknown' — we
        // will say so rather than pick a story.
      }
    }

    // The server row is the new baseline; anything that did not land stays in the
    // draft, character for character.
    const fresh = draftOf(next);
    setProfile(next);
    setDraft({
      name: profileOutcome === 'saved' ? fresh.name : draft.name,
      color: profileOutcome === 'saved' ? fresh.color : draft.color,
      notify: notifyOutcome === 'saved' ? fresh.notify : draft.notify,
    });

    // Say exactly what happened to each write, and give each one its own reason —
    // "saved", "didn't save", or the honest third answer, "we could not find out".
    const writes: { parts: string[]; outcome: Outcome; reason: string | null }[] = [];
    if (wantProfile) writes.push({ parts: profileParts, outcome: profileOutcome, reason: profileReason });
    if (wantNotify) {
      writes.push({ parts: ['your notification settings'], outcome: notifyOutcome, reason: notifyReason });
    }

    const allSaved = writes.every((w) => w.outcome === 'saved');
    const identityLanded = changed.identity && profileOutcome === 'saved';

    if (allSaved) {
      setSaved(true);
      setSaveError(null);
    } else {
      const saidSaved = writes.filter((w) => w.outcome === 'saved').flatMap((w) => w.parts);
      const bits: string[] = [];
      if (saidSaved.length > 0) bits.push(`${sentence(listOf(saidSaved))} saved.`);

      for (const w of writes) {
        if (w.outcome === 'saved') continue;
        const claim =
          w.outcome === 'failed'
            ? `${sentence(listOf(w.parts))} didn’t save.`
            : `We couldn’t find out whether ${listOf(w.parts)} saved.`;
        bits.push(w.reason ? `${claim} ${w.reason}` : claim);
      }

      bits.push('Nothing you changed here is lost — press Save to try again.');
      setSaveError(bits.join(' '));
    }

    // The banner is about what the rest of the app is now showing stale — so only for
    // an identity change that actually landed.
    if (identityLanded) setIdentityChanged(true);

    setSaving(false);
  }

  // ------------------------------------------------------------- loading
  if (!profile || !draft) {
    if (loadError) {
      return (
        <div className="mx-auto max-w-2xl">
          <h1 className="font-display text-3xl font-semibold text-ink">You</h1>
          <div className="mt-6">
            <ErrorNote message={loadError} onRetry={() => void load()} />
          </div>
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-2xl space-y-6" aria-busy="true">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
        <p className="sr-only">Loading your profile…</p>
      </div>
    );
  }

  const color = isHex(draft.color) ? draft.color.trim().toLowerCase() : profile.member.color;

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-4">
      {/* Hero — their name, their color, their face. The one loud thing on the page. */}
      <header
        className="rounded-card border border-border p-5 sm:p-6"
        style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, var(--color-surface))` }}
      >
        <div className="flex items-center gap-4">
          {profile.avatar_url && !heroBroken ? (
            <img
              src={profile.avatar_url}
              alt=""
              onError={() => setHeroBroken(true)}
              className="h-16 w-16 shrink-0 rounded-full border-2 object-cover"
              style={{ borderColor: color }}
            />
          ) : (
            <InitialAvatar
              name={draft.name || profile.member.name}
              color={color}
              className="h-16 w-16 border-2 border-white/60 text-2xl"
            />
          )}
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold text-ink">
              {draft.name.trim() || profile.member.name}
            </h1>
            <p className="text-sm text-ink-soft">
              This is your space. Change your picture, your color, and what you hear about.
            </p>
          </div>
        </div>
      </header>

      <Section title="Your picture" hint="It shows up next to your name all over the app.">
        <AvatarField
          profile={profile}
          name={draft.name || profile.member.name}
          color={color}
          onSaved={adoptAvatarOnly}
        />
      </Section>

      <Section title="Your name and color">
        <div className="flex flex-col gap-6">
          <Field
            label="Your name"
            hint="What everyone in the house sees."
            error={nameError}
          >
            {(props) => (
              <TextInput
                {...props}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                maxLength={NAME_MAX + 10} // let them overshoot, then tell them
                autoComplete="off"
              />
            )}
          </Field>

          <ColorField
            value={draft.color}
            onChange={(hex) => setDraft({ ...draft, color: hex })}
            error={colorError}
          />
        </div>
      </Section>

      {/* The home-screen layout (which cards, what size, what order) is edited on the
          home screen itself — "Edit layout" there writes member_prefs.home_layout. The old
          per-card show/hide picker lived here but wrote a field the hub no longer reads. */}

      <Section title="What you hear about" hint="Everything is on until you turn it off.">
        <NotificationsField
          role={role}
          value={draft.notify}
          onChange={(type, enabled) =>
            setDraft({ ...draft, notify: { ...draft.notify, [type]: enabled } })
          }
        />
      </Section>

      {/* Save bar. Sticky, above the phone's bottom nav, and it always says which of
          the three states it is in: nothing to save / unsaved changes / saved. */}
      {/* Opaque, not translucent. At bg-surface/95 the section heading underneath bled
          through the bar and the whole thing read as a rendering bug. A floating bar has
          to look like it's floating: solid ground and a real shadow. */}
      <div className="sticky bottom-24 z-10 md:bottom-4">
        <div className="flex flex-col gap-3 rounded-card border border-border bg-surface p-3 shadow-[0_8px_30px_-8px_rgb(35_38_31/0.35)]">
          {saveError && <AlertNote message={saveError} />}

          <div className="flex items-center justify-between gap-3">
            <p
              aria-live="polite"
              className={cx(
                'min-w-0 flex-1 text-sm',
                saved && !changed.any ? 'font-medium text-sage' : 'text-ink-soft'
              )}
            >
              {changed.any
                ? 'You have changes that aren’t saved yet.'
                : saved
                  ? '✓ Saved.'
                  : 'Everything here is saved.'}
            </p>

            <div className="flex shrink-0 items-center gap-2">
              {changed.any && (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDraft(draftOf(profile));
                    setSaveError(null);
                    setSaved(false);
                  }}
                  disabled={saving}
                >
                  Undo
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => void save()}
                disabled={saving || !changed.any || Boolean(nameError) || Boolean(colorError)}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {/* The header initial and color come from the identity loaded at startup, so
              they only catch up on a reload. Say that instead of letting it look broken. */}
          {identityChanged && !changed.any && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
              <p className="text-sm text-ink-soft">
                Saved. Refresh to see your new name and color everywhere else.
              </p>
              <Button variant="secondary" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
